import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { api } from '@/api/client';
import { User, LogOut, Coffee, Heart, Pencil, AlertCircle, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ReviewCard from '@/components/coffee/ReviewCard';
import EditReviewModal from '@/components/coffee/EditReviewModal';
import RequestChangeModal from '@/components/coffee/RequestChangeModal';
import EditProfileModal from '@/components/profile/EditProfileModal';
import MokaPotBadge from '@/components/coffee/MokaPotBadge';
import { Skeleton } from '@/components/ui/skeleton';

export default function MyPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [bioInput, setBioInput] = useState('');
  const [reviews, setReviews] = useState([]);
  const [coffeeMap, setCoffeeMap] = useState({});
  const [likeCounts, setLikeCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [editingReview, setEditingReview] = useState(null);
  const [requestModal, setRequestModal] = useState(null);
  const [editingProfile, setEditingProfile] = useState(false);

  useEffect(() => {
    api.auth.me()
      .then(async (u) => {
        setUser(u);
        setBioInput(u.bio || '');
        const revs = await api.entities.Review.filter({ reviewer_id: u.id }, '-created_date', 50);
        setReviews(revs);
        if (revs.length > 0) {
          const reviewIds = revs.map((r) => r.id);
          const [coffees, likes] = await Promise.all([
            api.entities.Coffee.filter({ id: [...new Set(revs.map((r) => r.coffee_id))] }),
            // Only the likes on this user's own reviews, rather than every like
            // in the database as the Base44 version did.
            api.entities.ReviewLike.filter({ review_id: reviewIds }),
          ]);
          const map = {};
          coffees.forEach((c) => (map[c.id] = c));
          setCoffeeMap(map);
          const counts = {};
          likes.forEach((l) => { counts[l.review_id] = (counts[l.review_id] || 0) + 1; });
          setLikeCounts(counts);
        }
        setLoading(false);
      })
      .catch(() => {
        navigate('/login');
      });
  }, []);

  // Real-time subscription for like counts
  useEffect(() => {
    const unsubscribe = api.entities.ReviewLike.subscribe((event) => {
      if (event.type === 'create') {
        const reviewId = event.data.review_id;
        setLikeCounts((prev) => ({ ...prev, [reviewId]: (prev[reviewId] || 0) + 1 }));
      } else if (event.type === 'delete') {
        const reviewId = event.data.review_id;
        setLikeCounts((prev) => ({ ...prev, [reviewId]: Math.max((prev[reviewId] || 1) - 1, 0) }));
      }
    });
    return () => unsubscribe();
  }, []);

  const handleSaveBio = async () => {
    await api.auth.updateMe({ bio: bioInput });
    setUser((u) => ({ ...u, bio: bioInput }));
  };

  const handleProfileSaved = async () => {
    const u = await api.auth.me();
    setUser(u);
  };

  const handleLogout = () => {
    api.auth.logout('/');
  };

  const handleEditReview = (review) => {
    setEditingReview(review);
  };

  const handleDeleteReview = async (reviewId) => {
    await api.entities.Review.delete(reviewId);
    setReviews(reviews.filter((r) => r.id !== reviewId));
    // The coffee's average_rating and this profile's badge are recalculated by
    // the reviews trigger, in the same transaction as the delete.
  };

  const handleReviewUpdated = async () => {
    const revs = await api.entities.Review.filter({ reviewer_id: user.id }, '-created_date', 50);
    setReviews(revs);
    setEditingReview(null);
  };

  const handleRequestSubmitted = () => {
    setRequestModal(null);
  };

  const avgRating = reviews.length
    ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1)
    : null;

  const totalLikes = reviews.reduce((s, r) => s + (likeCounts[r.id] || 0), 0);

  return (
    <div className="min-h-screen">
      {/* Profile header */}
      <div className="px-5 pt-14 pb-8 md:pt-20 md:pb-12" style={{ background: 'linear-gradient(135deg, #2c1a0e 0%, #3d2210 40%, #1a0f08 100%)' }}>
        <div className="max-w-5xl mx-auto">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full flex items-center justify-center border-2 shrink-0 overflow-hidden" style={{ borderColor: '#c9a96e', background: 'rgba(201,169,110,0.1)' }}>
              {user?.avatar_url ? (
                <img src={user.avatar_url} alt="avatar" className="w-full h-full object-cover" />
              ) : (
                <User size={28} style={{ color: '#c9a96e' }} strokeWidth={1.5} />
              )}
            </div>
            <div>
              <h1 className="font-heading font-semibold text-2xl flex items-center gap-2" style={{ color: '#f0e6d3' }}>
                {loading ? '...' : user?.display_name || user?.full_name || 'Coffee Lover'}
                {!loading && <MokaPotBadge reviewCount={reviews.length} size={22} />}
              </h1>
              {user?.bio && (
                <p className="text-xs mt-1" style={{ color: 'rgba(240,230,211,0.5)' }}>
                  {user.bio}
                </p>
              )}
            </div>
          </div>
          <div className="flex flex-col items-end gap-1.5">
            <button
              onClick={() => setEditingProfile(true)}
              className="flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all hover:opacity-80 w-full"
              style={{ border: '1px solid rgba(201,169,110,0.4)', color: 'rgba(240,230,211,0.8)', background: 'rgba(201,169,110,0.08)' }}
            >
              <Pencil size={13} />
              Edit Profile
            </button>
            {user?.role === 'admin' && (
              <Link
                to="/admin"
                className="flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all hover:opacity-80 w-full"
                style={{ border: '1px solid rgba(201,169,110,0.6)', color: '#c9a96e', background: 'rgba(201,169,110,0.15)' }}
              >
                <Shield size={13} />
                Admin
              </Link>
            )}
            <button
              onClick={handleLogout}
              className="flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all hover:opacity-80 w-full"
              style={{ border: '1px solid rgba(201,169,110,0.4)', color: 'rgba(240,230,211,0.8)', background: 'rgba(201,169,110,0.08)' }}
            >
              <LogOut size={13} />
              Log out
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="flex gap-4 mt-6">
          <StatChip icon={Coffee} label="Reviews" value={reviews.length} />
          <StatChip icon={Heart} label="Likes" value={totalLikes} />
        </div>
        </div>
      </div>

      <div className="px-4 py-6 space-y-4 max-w-5xl mx-auto">
        <div className="flex items-center justify-between">
          <h2 className="font-heading font-semibold text-base">My Reviews</h2>
          <Link to="/add">
            <Button size="sm" variant="outline" className="rounded-xl text-xs">
              + Add Review
            </Button>
          </Link>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {Array(4).fill(0).map((_, i) => <Skeleton key={i} className="h-32 rounded-2xl" />)}
          </div>
        ) : reviews.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-6 items-stretch">
            {reviews.map((review) => {
              const coffee = coffeeMap[review.coffee_id];
              return (
                <div key={review.id} className="flex flex-col h-full">
                  {coffee && (
                    <div className="mb-2 shrink-0 bg-muted rounded-xl px-3 py-1.5 border border-border flex items-center justify-between">
                      <Link
                        to={`/coffee/${coffee.id}`}
                        className="text-xs font-semibold text-primary hover:underline"
                      >
                        {coffee.name} · {coffee.cafe_name}
                      </Link>
                      <button
                        onClick={() => setRequestModal({ review, coffee })}
                        className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground transition-colors"
                      >
                        <AlertCircle size={11} />
                        Request Change
                      </button>
                    </div>
                  )}
                  <ReviewCard
                    review={review}
                    user={user}
                    initialLikeCount={likeCounts[review.id] || 0}
                    isOwner={true}
                    reviewerReviewCount={reviews.length}
                    reviewerAvatarUrl={user?.avatar_url}
                    linkToProfile={false}
                    onEdit={() => handleEditReview(review)}
                    onDelete={() => handleDeleteReview(review.id)}
                  />
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-16 border border-dashed border-border rounded-2xl">
            <Coffee size={36} className="mx-auto mb-3 text-muted-foreground opacity-40" strokeWidth={1} />
            <p className="text-sm text-muted-foreground">You haven't written any reviews yet.</p>
            <Link to="/search">
              <Button variant="link" className="text-primary mt-1 text-sm">
                Discover coffees →
              </Button>
            </Link>
          </div>
        )}
      </div>

      {editingReview && (
        <EditReviewModal
          review={editingReview}
          onClose={() => setEditingReview(null)}
          onSubmitted={handleReviewUpdated}
        />
      )}

      {requestModal && (
        <RequestChangeModal
          review={requestModal.review}
          coffee={requestModal.coffee}
          user={user}
          onClose={() => setRequestModal(null)}
          onSubmit={handleRequestSubmitted}
        />
      )}

      {editingProfile && (
        <EditProfileModal
          user={user}
          onClose={() => setEditingProfile(false)}
          onSaved={handleProfileSaved}
        />
      )}
    </div>
  );
}

function StatChip({ icon: IconComp, label, value }) {
  return (
    <div className="rounded-xl px-4 py-2.5 flex items-center gap-2" style={{ background: 'rgba(201,169,110,0.1)', border: '1px solid rgba(201,169,110,0.2)' }}>
      <IconComp size={15} style={{ color: '#c9a96e' }} strokeWidth={1.5} />
      <div>
        <p className="font-bold text-base leading-tight" style={{ color: '#f0e6d3' }}>{value}</p>
        <p className="text-[10px]" style={{ color: 'rgba(240,230,211,0.5)' }}>{label}</p>
      </div>
    </div>
  );
}